import React, { useState } from "react";
import { Modal, Button, Upload, Input, Tabs, message, Tooltip } from "antd";
import { DeleteOutlined, FileOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { uloadLegalReport } from "../../apis/LegalCaseAnalyserAPI";
import "./fileupload.scss";
import loader from '../../assets/images/loader.gif';  // Path to your custom loader gif


const { TabPane } = Tabs;

const FileUploadComponent = ({ onUploadSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [caseName, setCaseName] = useState("");
  const [activeTab, setActiveTab] = useState("complaint");
  const [files, setFiles] = useState({
    complaint: [],
    repudiation: [],
    evidence: [],
  });
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    setCaseName("");
    setFiles({
      complaint: [],
      repudiation: [],
      evidence: [],
    });
    setActiveTab("complaint");
  };

  const handleFileChange = (info, type) => {
    const { fileList } = info;


    // Remove duplicate files based on name and lastModified
    const uniqueFilesMap = new Map();
    fileList.forEach((file) => {
      const key = `${file.name}-${file.lastModified}`;
      if (uniqueFilesMap.has(key)) {
        // Duplicate detected
        message.error(`File "${file.name}" already selected, please select another file`);
      } else {
        uniqueFilesMap.set(key, file);
      }
    });


    const uniqueFiles = Array.from(uniqueFilesMap.values());

    // File validation logic for each type
    let validFiles = uniqueFiles.filter((file) => {
      if (type === "complaint") {
        // //console.log()
        const isValid =
          file.type === "application/pdf" ||
          file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        if (!isValid) {
          // message.error("Complaint Letter must be a PDF or DOCX file.");
          return false;
        }
      } else if (type === "repudiation") {
        const isValid = file.type === "application/pdf";
        if (!isValid) {
          // message.error("Repudiation Letter must be a PDF file.");
          return false;
        }
      } else if (type === "evidence") {
        const allowedTypes = [
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        const isValid = allowedTypes.includes(file.type);
        if (!isValid) {
          // message.error("Evidence files must be PDF, JPG, PNG, DOCX, or JPEG.");
          return false;
        }
      }
      return true; // Valid file
    });

    if (type === 'complaint' || type === 'repudiation') {
      if (validFiles.length > 1) {
        message.warning(`Only one file is allowed for ${type === 'complaint' ? 'Complaint Letter' : 'Repudiation Letter'}`);
      }
      validFiles = validFiles.slice(0, 1);
    }


    setFiles((prev) => ({
      ...prev,
      [type]: validFiles,
    }));
  };


  const handleFileRemove = (type, file) => {
    setFiles((prev) => ({
      ...prev,
      [type]: prev[type].filter((f) => f.uid !== file.uid),
    }));
  };

  const handleSubmit = async () => {
    if (!caseName) {
      message.error("Please enter a case name.");
      return;
    }

    const complaintFile = files.complaint[0] ?.originFileObj || null;
    const repudiationFile = files.repudiation[0] ?.originFileObj || null;
    const evidenceFiles = files.evidence;

    if (!complaintFile) {
      message.error("Complaint Letter is required.");
      return;
    }

    const formData = new FormData();
    formData.append("casename", caseName);
    formData.append("complaint", complaintFile);

    if (repudiationFile) {
      formData.append("repudiation", repudiationFile);
    }

    evidenceFiles.forEach((file) => {
      formData.append("evidence", file.originFileObj || null);
    });

    setLoading(true);
    try {
      const response = await dispatch(uloadLegalReport(formData));

      const resStatus = response ?.payload ?.statusCode;
      const resData = response ?.payload ?.data;

      if (resStatus === 200) {
        if (resData === "Duplicate case") {
          message.warning("Case with this name already exists.");
          return;
        }

        message.success("Case uploaded successfully!");
        if (onUploadSuccess) onUploadSuccess();

        setIsModalOpen(false);
        setCaseName("");
        setFiles({ complaint: [], repudiation: [], evidence: [] });
        setActiveTab("complaint");

      } else {
        message.error("Failed to upload the case.");
      }
    } catch (error) {
      message.error("An error occurred during the upload.");
    } finally {
      setLoading(false);
    }
  };

  // Upload props with file type restrictions
  const complaintUploadProps = {
    beforeUpload: (file) => {
      const isValid = file.type === "application/pdf" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      if (!isValid) {
        message.error("Complaint Letter must be a PDF or DOCX file.");
      }
      return isValid;
    },
    onChange: (info) => handleFileChange(info, "complaint"),
    showUploadList: false,
  };

  const repudiationUploadProps = {
    beforeUpload: (file) => {
      const isValid = file.type === "application/pdf";
      if (!isValid) {
        message.error("Repudiation Letter must be a PDF file.");
      }
      return isValid;
    },
    onChange: (info) => handleFileChange(info, "repudiation"),
    showUploadList: false,
  };

  const evidenceUploadProps = {
    beforeUpload: (file) => {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const isValid = allowedTypes.includes(file.type);
      if (!isValid) {
        message.error("Evidence files must be PDF, JPG, PNG, DOCX, or JPEG.");
      }
      return isValid;
    },
    onChange: (info) => handleFileChange(info, "evidence"),
    showUploadList: false,
  };

  return (
    <div className="upload-documents-container">

      <Tooltip title="Click on the button to create a new case ">
        <button className="btn-outlined" onClick={showModal}>
          <div className='btn-outlined-content'>
            <div className='text'>  Create Case </div>
          </div>
        </button>
      </Tooltip>

      <Modal
        title="Create Case"
        open={isModalOpen}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="submit" type="primary" onClick={handleSubmit} disabled={loading}>
            Create
    </Button>,
        ]}
        maskClosable={false}
      >
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <img className="gif-loader" src={loader} alt="Loading..." />
          </div>
        ) : (
            <>
              <Input
                value={caseName}
                onChange={(e) => setCaseName(e.target.value)}
                placeholder="Enter Case Name"
                className="input-case-name"
              />
              <p className="uploadtitle">Upload Documents</p>
              <p style={{ color: "grey", fontSize: "12px",marginTop:"-9px" }}>
                Click on the tab to upload the required documents
      </p>

              <Tabs activeKey={activeTab} onChange={setActiveTab} tabBarGutter={20} tabPosition="left" style={{ marginLeft: "-22px", height: "10%" }} >
                <TabPane tab="Complaint Letter" key="complaint">
                  <Upload.Dragger
                    accept=".doc,.pdf"
                    {...complaintUploadProps}
                    fileList={files.complaint}
                  >
                    <p>Drag and drop file here</p>
                    <p className="file-types">File size limit: 200MB per file</p>
                    <p>Supported formats: PDF, DOCX</p>
                  </Upload.Dragger>
                  <div className="file-list-container">
                    {files.complaint.map((file) => (
                      <div className="file-list" key={file.uid}>
                        <FileOutlined className="file-icon" />
                        <span className="file-name">{file.name}</span>
                        <DeleteOutlined
                          className="delete-icon"
                          onClick={() => handleFileRemove("complaint", file)}
                        />
                      </div>
                    ))}
                  </div>
                </TabPane>

                <TabPane tab="Repudiation Letter" key="repudiation">
                  <Upload.Dragger
                    {...repudiationUploadProps}
                    fileList={files.repudiation}
                  >
                    <p>Drag and drop file here</p>
                    <p className="file-types">File size limit: 200MB per file</p>
                    <p>Supported formats: PDF</p>
                  </Upload.Dragger>
                  <div className="file-list-container">
                    {files.repudiation.map((file) => (
                      <div className="file-list" key={file.uid}>
                        <FileOutlined className="file-icon" />
                        <span className="file-name">{file.name}</span>
                        <DeleteOutlined
                          className="delete-icon"
                          onClick={() => handleFileRemove("repudiation", file)}
                        />
                      </div>
                    ))}
                  </div>
                </TabPane>

                <TabPane tab="Evidences" key="evidence">
                  <Upload.Dragger
                    {...evidenceUploadProps}
                    fileList={files.evidence}
                  >
                    <p>Drag and drop file here</p>
                    <p className="file-types">File size limit: 200MB per file</p>
                    <p>Supported formats: PDF, JPG, PNG, DOCX, JPEG</p>
                  </Upload.Dragger>
                  <div className="file-list-container">
                    {files.evidence.map((file) => (
                      <div className="file-list" key={file.uid}>
                        <FileOutlined className="file-icon" />
                        <span className="file-name">{file.name}</span>
                        <DeleteOutlined
                          className="delete-icon"
                          onClick={() => handleFileRemove("evidence", file)}
                        />
                      </div>
                    ))}
                  </div>
                </TabPane>
              </Tabs>
            </>
          )}
      </Modal>

    </div>
  );
};

export default FileUploadComponent;
